<template>
  <b-container fluid class="project-graph-container p-3">
    <b-row>
      <b-col>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h2 class="mb-0">Visualisation des Relations de Projets</h2>
          <div class="reference-date-info">
            <small class="text-muted">Date de référence pour les filtres :</small><br>
            <strong class="text-primary">{{ formattedReferenceDate }}</strong>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Sélecteur de node -->
    <b-row class="mb-3">
      <b-col cols="6">
        <b-form-group label="Sélectionner un projet :" label-for="node-select" class="mb-0">
          <v-select 
            id="node-select"
            v-model="selectedNodeId"
            :options="nodeOptions"
            label="text"
            :reduce="(option) => option.value"
            placeholder="-- Choisir un projet --"
            @input="onNodeSelect"
            style="width: 100%"
          />
        </b-form-group>
      </b-col>
    </b-row>
    
    <!-- Contrôles et Filtres -->
    <b-row class="mb-3">
      <b-col>
        <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
          <b-button @click="generate" variant="outline-primary">Regénérer graph</b-button>
          <b-button variant="primary" @click="resetZoom">Réinitialiser le zoom</b-button>
          
          <div class="d-flex align-items-center ms-3">
            <b-form-checkbox
              id="filter-connected-nodes"
              v-model="showOnlyConnectedNodes"
              switch
              @change="updateGraphVisibility"
              class="custom-switch"
            >
              Afficher seulement les nœuds connectés
            </b-form-checkbox>
          </div>
          
          <div class="ms-auto">
            <b-button 
              variant="outline-secondary" 
              @click="showFilters = !showFilters"
              class="d-flex align-items-center"
            >
              🔍 Filtres
              <small v-if="hasActiveFilters" class="ms-2 text-muted">
                ({{ filteredNodes.length }}/{{ allNodes.length }})
              </small>
              <b-icon :icon="showFilters ? 'chevron-up' : 'chevron-down'" class="ms-1"></b-icon>
            </b-button>
          </div>
        </div>
        
        <!-- Panneau de filtres -->
        <b-collapse v-model="showFilters" id="filters-collapse">
          <b-card class="filter-panel mb-3">
            <b-card-body class="py-2">
              <b-row class="gx-2 align-items-end flex-nowrap">
                <!-- Filtre par nom -->
                <b-col style="min-width:180px">
                  <b-form-group label="Nom du projet :" label-size="sm" class="mb-0">
                    <b-form-input
                      v-model="filters.name"
                      placeholder="ex: xchange api (ET logique)"
                      size="sm"
                      @input="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Filtre par API -->
                <b-col style="min-width:140px">
                  <b-form-group label="API contient :" label-size="sm" class="mb-0">
                    <b-form-input
                      v-model="filters.apiCall"
                      placeholder="ex: GET origami"
                      size="sm"
                      @input="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Filtre par base de données -->
                <b-col style="min-width:140px">
                  <b-form-group label="DB contient :" label-size="sm" class="mb-0">
                    <b-form-input
                      v-model="filters.dbCall"
                      placeholder="ex: INSERT xchange"
                      size="sm"
                      @input="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Filtre par date traitement -->
                <b-col style="min-width:140px">
                  <b-form-group label="Traitement depuis :" label-size="sm" class="mb-0">
                    <b-form-select
                      v-model="filters.dateFilter"
                      :options="dateFilterOptions"
                      size="sm"
                      @change="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Filtre par date test éditeur -->
                <b-col style="min-width:140px">
                  <b-form-group label="Test éditeur depuis :" label-size="sm" class="mb-0">
                    <b-form-select
                      v-model="filters.testDateFilter"
                      :options="dateFilterOptions"
                      size="sm"
                      @change="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Filtre actif depuis (OU) -->
                <b-col style="min-width:155px">
                  <b-form-group label-size="sm" class="mb-0">
                    <template #label>
                      Traitement <strong>ou</strong> test éditeur depuis :
                    </template>
                    <b-form-select
                      v-model="filters.activeSinceFilter"
                      :options="dateFilterOptions"
                      size="sm"
                      @change="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <!-- Bouton reset -->
                <b-col style="min-width:160px">
                  <b-form-group label="Actions :" label-size="sm" class="mb-0">
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="resetFilters"
                      class="w-100 mb-1"
                    >
                      Réinitialiser les filtres
                    </b-button>
                    <b-button
                      variant="outline-success"
                      size="sm"
                      @click="openTabulator"
                      class="w-100"
                    >
                      📊 Voir dans un tableau
                    </b-button>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-collapse>
        
        <!-- Tableau d'export -->
        <b-collapse v-model="showTabulatorSection" id="tabulator-collapse">
          <b-card class="mb-3">
            <b-card-header class="d-flex justify-content-between align-items-center py-2">
              <strong>Tableau des projets filtrés ({{ filteredNodes.length }})</strong>
              <b-button size="sm" variant="outline-secondary" @click="showTabulatorSection = false">✕ Fermer</b-button>
            </b-card-header>
            <b-card-body class="p-2">
              <edit_tabulator :extern_file="false" ref="projects_tabulator"></edit_tabulator>
            </b-card-body>
          </b-card>
        </b-collapse>
      </b-col>
    </b-row>
    
    <!-- Contrôles -->
    <b-row class="mb-3" style="display: none;">
      <b-col>
        <b-button @click="generate">Reénérer graph</b-button>&nbsp;
        <b-button variant="primary" @click="resetZoom">
          Réinitialiser le zoom
        </b-button>
        <div class="d-inline-block ms-3">
          <b-form-checkbox
            id="filter-connected-nodes"
            v-model="showOnlyConnectedNodes"
            switch
            @change="updateGraphVisibility"
            class="custom-switch"
          >
            Afficher seulement les nœuds connectés
          </b-form-checkbox>
        </div>
      </b-col>
    </b-row>

    <div v-if="launch" class="center_spinner">
				<fade-loader></fade-loader>
				<br /><br />
			</div>

    <!-- Container principal avec graphique et panneau de détails -->
    <b-row no-gutters class="main-row">
      <!-- Container du graphique -->
      <b-col cols="8" class="graph-section pe-2">
        <b-card no-body class="h-100 border-0">
          <div ref="graphContainer" class="graph-container"></div>
        </b-card>
      </b-col>

      <!-- Panneau de détails -->
      <b-col cols="4" class="details-section ps-2">
        <b-card 
          :class="{ 'selected': selectedNode }" 
          class="details-panel h-100 border"
        >
          <b-card-body v-if="selectedNode" class="compact-body p-0">
            <div class="project-header">
              <b-card-title class="mb-3">{{ selectedNode.name }}</b-card-title>
            </div>
            <div class="project-content">
            
            <b-list-group flush class="mb-2">
              <b-list-group-item class="d-flex justify-content-between align-items-center px-3 py-2">
                <strong>ID:</strong>
                <span>{{ selectedNode.id }}</span>
              </b-list-group-item>

              <!-- Date du dernier traitement -->
              <b-list-group-item v-if="getLastTreatmentDate(selectedNode)" class="px-3 py-2">
                <strong>Dernier traitement:</strong><br>
                <small class="text-muted">{{ getLastTreatmentDate(selectedNode) }}</small>
              </b-list-group-item>

              <!-- Date du dernier test éditeur -->
              <b-list-group-item v-if="selectedNode.project && selectedNode.project.details && selectedNode.project.details.test_date" class="px-3 py-2">
                <strong>Dernier test éditeur:</strong><br>
                <small class="text-muted">{{ moment(selectedNode.project.details.test_date).format('DD MMMM YYYY à HH:mm') }}</small>
              </b-list-group-item>
            </b-list-group>

            <!-- Description du projet -->
            <div v-if="getProjectDescription(selectedNode)" class="mb-1">
              <b-card-sub-title class="mb-1" style="font-size: 0.9rem;">Description</b-card-sub-title>
              <div class="description-container" @click="showDescriptionModal" style="cursor: pointer;" title="Cliquer pour voir en grand">
                <small>{{ getProjectDescription(selectedNode) }}</small>
              </div>
            </div>
            

            <!-- Appels API -->
            <div v-if="getApiCalls(selectedNode).length > 0" class="mb-2">
              <b-card-sub-title class="mb-1" style="font-size: 0.9rem;">
                Appels API 
                <b-badge variant="info">{{ getApiCalls(selectedNode).length }}</b-badge>
              </b-card-sub-title>
              <div class="api-calls-container">
                <div v-for="(call, index) in getApiCalls(selectedNode)" :key="`api-${index}`" class="api-call-item">
                  <small class="fw-medium">{{ call.method }}</small>
                  <br>
                  <small class="text-muted" style="word-break: break-all;">{{ call.url }}</small>
                </div>
              </div>
            </div>

            <!-- Appels en base -->
            <div v-if="getDatabaseCalls(selectedNode).length > 0" class="mb-2">
              <b-card-sub-title class="mb-1" style="font-size: 0.9rem;">
                Appels en base 
                <b-badge variant="warning">{{ getDatabaseCalls(selectedNode).length }}</b-badge>
              </b-card-sub-title>
              <div class="db-calls-container">
                <div v-for="(call, index) in getDatabaseCalls(selectedNode)" :key="`db-${index}`" class="db-call-item">
                  <small class="fw-medium">{{ call.operation }}</small>
                  <br>
                  <small class="text-muted">{{ call.table }}</small>
                </div>
              </div>
            </div>
            
            <!-- Relations sortantes -->
            <b-card-sub-title class="mb-1" style="font-size: 0.9rem;">
              Relations sortantes 
              <b-badge variant="secondary">{{ outgoingRelations.length }}</b-badge>
            </b-card-sub-title>
            
            <b-list-group v-if="outgoingRelations.length > 0" flush class="mb-2 relations-list">
              <b-list-group-item 
                v-for="(relation, index) in outgoingRelations" 
                :key="`out-${index}-${getNodeId(relation.source)}-${getNodeId(relation.target)}`"
                class="px-2 py-1 clickable-relation"
                :class="{ 'filtered-out': !relation.targetMatchesFilters }"
                @click="focusOnNode(getNodeId(relation.target))"
              >
                <div class="d-flex align-items-center">
                  <span class="text-muted me-2">→</span>
                  <span class="fw-medium me-2">{{ getNodeName(relation.target) }}</span>
                  <small class="text-muted">({{ linkTypes[relation.type] || relation.type }})</small>
                </div>
              </b-list-group-item>
            </b-list-group>
            
            <b-alert v-else variant="light" show class="py-1 mb-2 mx-2">
              <small>Aucune relation sortante</small>
            </b-alert>

            <!-- Relations entrantes -->
            <b-card-sub-title class="mb-1" style="font-size: 0.9rem;">
              Relations entrantes 
              <b-badge variant="secondary">{{ incomingRelations.length }}</b-badge>
            </b-card-sub-title>
            
            <b-list-group v-if="incomingRelations.length > 0" flush class="relations-list">
              <b-list-group-item 
                v-for="(relation, index) in incomingRelations" 
                :key="`in-${index}-${getNodeId(relation.source)}-${getNodeId(relation.target)}`"
                class="px-2 py-1 clickable-relation"
                :class="{ 'filtered-out': !relation.sourceMatchesFilters }"
                @click="focusOnNode(getNodeId(relation.source))"
              >
                <div class="d-flex align-items-center">
                  <span class="text-muted me-2">←</span>
                  <span class="fw-medium me-2">{{ getNodeName(relation.source) }}</span>
                  <small class="text-muted">({{ linkTypes[relation.type] || relation.type }})</small>
                </div>
              </b-list-group-item>
            </b-list-group>
            
            <b-alert v-else variant="light" show class="py-1 mx-2">
              <small>Aucune relation entrante</small>
            </b-alert>
            
            </div> <!-- Fermeture de project-content -->
          </b-card-body>
          
          <b-card-body v-else class="text-center">
            <div class="text-muted mb-2" style="font-size: 2rem;">🖱️</div>
            <p class="text-muted mb-0">Cliquez sur un projet pour voir ses détails</p>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal pour la description du projet -->
    <b-modal 
      id="description-modal" 
      v-model="showDescriptionModalFlag"
      :title="selectedNode ? selectedNode.name : 'Description du projet'"
      size="lg"
      centered
      ok-only
      ok-title="Fermer"
    >
      <div class="description-modal-content">
        <p v-if="selectedNode" style="white-space: pre-wrap; line-height: 1.6;">
          {{ getProjectDescription(selectedNode) }}
        </p>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
	import documentation_service from "../services/documentation.js";

import * as d3 from 'd3'
    import FadeLoader from "vue-spinner/src/FadeLoader.vue";
	import store from "../store";
	import edit_tabulator from "../components/edit_tabulator.vue";

export default {
  name: 'ProjectGraph',
  components: {
    FadeLoader,
    edit_tabulator
  },
  data() {
    return {
      launch: false,
      documentation: {},
      width: 800,
      height: 600,
      simulation: null,
      svg: null,
      selectedNode: null,
      selectedNodeId: null, // Pour le v-select
      showOnlyConnectedNodes: false, // Option pour filtrer les nœuds
      showFilters: false, // Pour l'accordéon des filtres
      
      // Système de filtres
      filters: {
        name: '',
        apiCall: '',
        dateFilter: 'all',
        dbCall: '',
        testDateFilter: 'all',
        activeSinceFilter: 'all'
      },
      dateFilterOptions: [
        { value: 'all', text: 'Toutes les dates' },
        { value: '1week', text: 'Depuis 1 semaine' },
        { value: '1month', text: 'Depuis 1 mois' },
        { value: '3months', text: 'Depuis 3 mois' },
        { value: '6months', text: 'Depuis 6 mois' },
        { value: '1year', text: 'Depuis 1 an' }
      ],
      filteredNodes: [], // Nœuds après application des filtres
      allNodes: [], // Tous les nœuds originaux
      
      showDescriptionModalFlag: false, // Pour contrôler l'affichage de la modal
      showTabulatorSection: false, // Pour afficher le tableau d'export
      
      nodes: [],
      links: [],
      nodeTypes: {
        backend: '#ff6b6b',
        frontend: '#4ecdc4',
        api: '#45b7d1',
        database: '#f9ca24',
        mobile: '#6c5ce7',
        default: '#95a5a6'
      },
      linkTypes: {
        depends: 'Dépend de',
        uses: 'Utilise',
        accesses: 'Accède à',
        stores: 'Stocke dans',
        shares: 'Partage avec',
        calls: 'Appelle'
      }
    }
  },
  computed: {
    outgoingRelations() {
      if (!this.selectedNode) return []
      return this.links.filter(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source
        return sourceId === this.selectedNode.id
      }).map(link => {
        // Ajoute une propriété pour indiquer si le nœud cible correspond aux filtres
        const targetId = typeof link.target === 'object' ? link.target.id : link.target
        const targetNode = this.allNodes.find(n => n.id === targetId)
        return {
          ...link,
          targetMatchesFilters: targetNode ? this.nodeMatchesFilters(targetNode) : false
        }
      })
    },
    incomingRelations() {
      if (!this.selectedNode) return []
      return this.links.filter(link => {
        const targetId = typeof link.target === 'object' ? link.target.id : link.target
        return targetId === this.selectedNode.id
      }).map(link => {
        // Ajoute une propriété pour indiquer si le nœud source correspond aux filtres
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source
        const sourceNode = this.allNodes.find(n => n.id === sourceId)
        return {
          ...link,
          sourceMatchesFilters: sourceNode ? this.nodeMatchesFilters(sourceNode) : false
        }
      })
    },
    relatedNodeIds() {
      if (!this.selectedNode) return new Set()
      const related = new Set()
      this.outgoingRelations.forEach(link => {
        const targetId = typeof link.target === 'object' ? link.target.id : link.target
        related.add(targetId)
      })
      this.incomingRelations.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source
        related.add(sourceId)
      })
      return related
    },
    nodeOptions() {
      return this.filteredNodes.map(node => ({
        value: node.id,
        text: node.name
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    
    // Computed properties pour les filtres
    hasActiveFilters() {
      return this.filters.name !== '' || 
             this.filters.apiCall !== '' || 
             this.filters.dbCall !== '' ||
             this.filters.dateFilter !== 'all' ||
             this.filters.testDateFilter !== 'all' ||
             this.filters.activeSinceFilter !== 'all'
    },
    
    // Date de référence (traitement le plus récent de tous les projets)
    mostRecentTreatmentDate() {
      if (!this.allNodes || this.allNodes.length === 0) {
        return null
      }
      
      let mostRecentDate = null
      this.allNodes.forEach(node => {
        const nodeDate = this.getLastTreatmentDateRaw(node)
        if (nodeDate && (!mostRecentDate || nodeDate > mostRecentDate)) {
          mostRecentDate = nodeDate
        }
      })
      
      return mostRecentDate
    },
    
    // Formatte la date de référence pour l'affichage
    formattedReferenceDate() {
      if (!this.mostRecentTreatmentDate) {
        return 'Aucune date de traitement trouvée'
      }
      
      // Utilise le même format que getLastTreatmentDate mais sans moment.js
      const date = this.mostRecentTreatmentDate
      const options = { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        locale: 'fr-FR'
      }
      
      return date.toLocaleDateString('fr-FR', options)
    }
  },
  async mounted() {
    this.initGraph()

    if (store.state.user.rules && !store.state.user.rules.doc) {
				this.$router.push({ name: "profile" });
			}
			
			this.documentation = JSON.parse(await documentation_service.get("json"));
      console.log(this.documentation)
      
      // Parse les données initiales
      this.parseDocumentationData()
      this.updateGraph()
      this.updateGraphVisibility() // Applique le filtre si activé

  },
  beforeUnmount() {
    if (this.simulation) {
      this.simulation.stop()
    }
  },
  methods: {
    // Méthodes de filtrage
    applyFilters() {
      if (!this.hasActiveFilters) {
        this.filteredNodes = [...this.allNodes]
      } else {
        this.filteredNodes = this.allNodes.filter(node => this.nodeMatchesFilters(node))
      }
      
      // Met à jour la visualisation
      this.updateGraphVisibility()
    },
    
    nodeMatchesFilters(node) {
      // Vérification de sécurité
      if (!node || !node.name) {
        return false
      }
      
      // Filtre par nom
      if (this.filters.name) {
        // Divise la recherche en mots-clés (logique AND)
        const searchTerms = this.filters.name.toLowerCase().split(' ').filter(term => term.trim() !== '')
        const nodeName = node.name.toLowerCase()
        
        // Vérifie que tous les termes de recherche sont présents dans le nom
        const nameMatches = searchTerms.every(term => nodeName.includes(term))
        
        if (!nameMatches) {
          return false
        }
      }
      
      // Filtre par appel API
      if (this.filters.apiCall) {
        const apiCalls = this.getApiCalls(node)
        // Divise la recherche en mots-clés (logique AND)
        const searchTerms = this.filters.apiCall.toLowerCase().split(' ').filter(term => term.trim() !== '')
        
        const hasMatchingApi = apiCalls.some(call => {
          // Combine toutes les propriétés recherchables en une seule chaîne
          const searchableContent = [
            (call.url && typeof call.url === 'string') ? call.url : '',
            (call.method && typeof call.method === 'string') ? call.method : ''
          ].join(' ').toLowerCase()
          
          // Vérifie que tous les termes de recherche sont présents
          return searchTerms.every(term => searchableContent.includes(term))
        })
        
        if (!hasMatchingApi) {
          return false
        }
      }
      
      // Filtre par appel de base de données
      if (this.filters.dbCall) {
        const dbCalls = this.getDatabaseCalls(node)
        // Divise la recherche en mots-clés (logique AND)
        const searchTerms = this.filters.dbCall.toLowerCase().split(' ').filter(term => term.trim() !== '')
        
        const hasMatchingDb = dbCalls.some(call => {
          // Combine toutes les propriétés recherchables en une seule chaîne
          const searchableContent = [
            (call.operation && typeof call.operation === 'string') ? call.operation : '',
            (call.table && typeof call.table === 'string') ? call.table : '',
            (call.fullQuery && typeof call.fullQuery === 'string') ? call.fullQuery : ''
          ].join(' ').toLowerCase()
          
          // Vérifie que tous les termes de recherche sont présents
          return searchTerms.every(term => searchableContent.includes(term))
        })
        
        if (!hasMatchingDb) {
          return false
        }
      }
      
      // Filtre par date traitement
      if (this.filters.dateFilter !== 'all') {
        const lastTreatmentDate = this.getLastTreatmentDateRaw(node)
        if (!lastTreatmentDate || !this.isDateWithinFilter(lastTreatmentDate, this.filters.dateFilter)) {
          return false
        }
      }

      // Filtre par date test éditeur
      if (this.filters.testDateFilter !== 'all') {
        const testDate = node.project && node.project.details && node.project.details.test_date
          ? new Date(node.project.details.test_date)
          : null
        if (!testDate || !this.isDateWithinFilter(testDate, this.filters.testDateFilter)) {
          return false
        }
      }

      // Filtre actif depuis (OU : last_treatment OU test_date)
      if (this.filters.activeSinceFilter !== 'all') {
        const lastTreatmentDate = this.getLastTreatmentDateRaw(node)
        const testDate = node.project && node.project.details && node.project.details.test_date
          ? new Date(node.project.details.test_date)
          : null
        const treatmentOk = lastTreatmentDate && this.isDateWithinFilter(lastTreatmentDate, this.filters.activeSinceFilter)
        const testOk = testDate && this.isDateWithinFilter(testDate, this.filters.activeSinceFilter)
        if (!treatmentOk && !testOk) {
          return false
        }
      }

      return true
    },
    
    isDateWithinFilter(dateObj, filterType) {
      if (!dateObj) return false
      
      // Accepte soit un objet Date soit une chaîne
      const date = dateObj instanceof Date ? dateObj : new Date(dateObj)
      
      // Vérifie que la date est valide
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateObj)
        return false
      }
      
      const referenceDate = new Date(this.mostRecentTreatmentDate)
      const diffTime = referenceDate - date
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      console.log(`Date filter check: ${date.toISOString()} vs reference date (${referenceDate.toISOString()}), diff: ${diffDays} days, filter: ${filterType}`)
      
      switch (filterType) {
        case '1week':
          return diffDays <= 7
        case '1month':
          return diffDays <= 30
        case '3months':
          return diffDays <= 90
        case '6months':
          return diffDays <= 180
        case '1year':
          return diffDays <= 365
        default:
          return true
      }
    },
    
    getLastTreatmentDateRaw(node) {
      if (!node.project) {
        return null
      }
      
      // D'abord, essaie avec last_treatment (structure principale)
      if (node.project.last_treatment) {
        return new Date(node.project.last_treatment)
      }
      
      // Sinon, cherche dans traitements comme backup
      if (node.project.traitements && Array.isArray(node.project.traitements)) {
        let latestDate = null
        node.project.traitements.forEach(traitement => {
          if (traitement.date_creation) {
            const date = new Date(traitement.date_creation)
            if (!latestDate || date > latestDate) {
              latestDate = date
            }
          }
        })
        return latestDate
      }
      
      return null
    },
    
    resetFilters() {
      this.filters.name = ''
      this.filters.apiCall = ''
      this.filters.dbCall = ''
      this.filters.dateFilter = 'all'
      this.filters.testDateFilter = 'all'
      this.filters.activeSinceFilter = 'all'
      this.applyFilters()
    },

    openTabulator() {
      const rows = this.filteredNodes.map(node => {
        const apiCalls = this.getApiCalls(node)
        const dbCalls = this.getDatabaseCalls(node)
        const description = this.getProjectDescription(node)

        // Relations sortantes et entrantes
        const outgoing = this.links.filter(l => {
          const sid = typeof l.source === 'object' ? l.source.id : l.source
          return sid === node.id
        }).map(l => {
          const tid = typeof l.target === 'object' ? l.target.id : l.target
          const target = this.allNodes.find(n => n.id === tid)
          return target ? target.name : tid
        }).join(', ')

        const incoming = this.links.filter(l => {
          const tid = typeof l.target === 'object' ? l.target.id : l.target
          return tid === node.id
        }).map(l => {
          const sid = typeof l.source === 'object' ? l.source.id : l.source
          const source = this.allNodes.find(n => n.id === sid)
          return source ? source.name : sid
        }).join(', ')

        const lastDateRaw = this.getLastTreatmentDateRaw(node)
        const lastDate = lastDateRaw ? this.moment(lastDateRaw).format('YYYY-MM-DD HH:mm:ss') : ''
        const testDate = node.project && node.project.details && node.project.details.test_date
          ? this.moment(node.project.details.test_date).format('YYYY-MM-DD HH:mm:ss')
          : ''

        return {
          id: node.id,
          libelle: node.name,
          description: description || '',
          dernier_traitement: lastDate || '',
          dernier_test_editeur: testDate,
          nb_appels_api: apiCalls.length,
          appels_api: apiCalls.map(c => c.method + ' ' + c.url).join(' | '),
          nb_appels_db: dbCalls.length,
          appels_db: dbCalls.map(c => c.operation + ' ' + c.table).join(' | '),
          relations_sortantes: outgoing,
          relations_entrantes: incoming
        }
      })

      this.showTabulatorSection = true
      this.$nextTick(() => {
        if (this.$refs.projects_tabulator) {
          this.$refs.projects_tabulator.set_data(rows)
        }
      })
    },

			async generate()
			{
				this.launch = true;
				await documentation_service.generate();
        this.documentation = JSON.parse(await documentation_service.get("json"));
        console.log(this.documentation)
        
        // Parse les données et met à jour le graphique
        this.parseDocumentationData()
        this.updateGraph()
        this.updateGraphVisibility() // Applique le filtre si activé
        
				this.launch = false;
		},

    onNodeSelect(nodeId) {
      if (nodeId) {
        // Utilise la même logique que focusOnNode pour être cohérent
        this.focusOnNode(nodeId)
      } else {
        this.clearSelection()
      }
    },

    parseDocumentationData() {
      if (!this.documentation || !this.documentation.projects) {
        console.warn('Pas de données de projets dans la documentation')
        return
      }

      // Réinitialise les données
      this.nodes = []
      this.links = []

      // Crée les nodes à partir des projets
      this.documentation.projects.forEach(project => {
        this.nodes.push({
          id: project.uid,
          name: project.libelle,
          type: this.determineProjectType(project),
          project: project
        })
      })

      // Crée les liens à partir des relations dans steps_details
      this.documentation.projects.forEach(project => {
        if (project.steps_details && project.steps_details.sub_project && project.steps_details.sub_project.project) {
          this.extractProjectLinks(project.uid, project.steps_details.sub_project.project)
        }
      })

      // Initialise les données pour les filtres
      this.allNodes = [...this.nodes]
      this.filteredNodes = [...this.nodes]

      // ('Nodes extraits:', this.nodes)
      // console.log('Links extraits:', this.links)
    },

    extractProjectLinks(sourceId, projectData) {
      //console.log("extractProjectLinks")
      //console.log(JSON.stringify(sourceId))
      //console.log(JSON.stringify(projectData))
      // Les clés de l'objet project sont les projets appelés
      //console.log(JSON.stringify(projectData.project))
      Object.keys(projectData).forEach(targetId => {
        // Vérifie que le projet cible existe dans nos nodes
        //console.log("targetId")
        //console.log(targetId)
        const targetExists = this.nodes.some(node => node.id === targetId)
        if (targetExists && sourceId !== targetId) {
          this.links.push({
            source: sourceId,
            target: targetId,
            type: 'calls'
          })
        }
      });
  },

    determineProjectType(project) {
      // Logique pour déterminer le type de projet basé sur ses propriétés
      // Vous pouvez adapter cette logique selon vos besoins
      /*if (project.libelle) {
        const libelle = project.libelle.toLowerCase()
        if (libelle.includes('api')) return 'api'
        if (libelle.includes('frontend') || libelle.includes('web')) return 'frontend'
        if (libelle.includes('backend') || libelle.includes('server')) return 'backend'
        if (libelle.includes('database') || libelle.includes('db')) return 'database'
        if (libelle.includes('mobile')) return 'mobile'
      }*/
      return 'default'
    },
    initGraph() {
      const container = this.$refs.graphContainer
      if (!container) return
      
      // Get container dimensions
      const containerRect = container.getBoundingClientRect()
      const width = containerRect.width - 2 // -2 for borders
      const height = containerRect.height - 2 // -2 for borders
      
      // Clear any existing SVG
      d3.select(container).selectAll("*").remove()

      // Create SVG with full container dimensions
      this.svg = d3.select(container)
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background-color', 'white')
        .style('display', 'block')

      // Add zoom behavior
      this.zoomBehavior = d3.zoom()
        .scaleExtent([0.1, 4])
        .on('zoom', (event) => {
          this.svg.select('.graph-group').attr('transform', event.transform)
        })

      this.svg.call(this.zoomBehavior)

      // Create main group for zoom/pan
      const g = this.svg.append('g').attr('class', 'graph-group')

      // Define arrow markers
      const defs = g.append('defs')
      
      // Main arrow marker (default)
      defs.append('marker')
        .attr('id', 'arrowhead')
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 10)
        .attr('refY', 5)
        .attr('markerWidth', 8)
        .attr('markerHeight', 8)
        .attr('orient', 'auto')
        .attr('markerUnits', 'strokeWidth')
        .append('path')
        .attr('d', 'M0,0 L0,10 L10,5 z')
        .attr('fill', '#333')
        .attr('stroke', 'none')

      // Green arrow marker for outgoing relations
      defs.append('marker')
        .attr('id', 'arrowhead-outgoing')
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 10)
        .attr('refY', 5)
        .attr('markerWidth', 8)
        .attr('markerHeight', 8)
        .attr('orient', 'auto')
        .attr('markerUnits', 'strokeWidth')
        .append('path')
        .attr('d', 'M0,0 L0,10 L10,5 z')
        .attr('fill', '#28a745')
        .attr('stroke', 'none')

      // Red arrow marker for incoming relations
      defs.append('marker')
        .attr('id', 'arrowhead-incoming')
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 10)
        .attr('refY', 5)
        .attr('markerWidth', 8)
        .attr('markerHeight', 8)
        .attr('orient', 'auto')
        .attr('markerUnits', 'strokeWidth')
        .append('path')
        .attr('d', 'M0,0 L0,10 L10,5 z')
        .attr('fill', '#dc3545')
        .attr('stroke', 'none')

      // Store dimensions for updateGraph
      this.currentWidth = width
      this.currentHeight = height
      
      this.updateGraph()
    },

    updateGraph() {
      if (!this.svg) return
      
      const g = this.svg.select('.graph-group')

      // Create force simulation avec collision améliorée pour éviter les superpositions
      this.simulation = d3.forceSimulation(this.nodes)
        .force('link', d3.forceLink(this.links).id(d => d.id).distance(150))
        .force('charge', d3.forceManyBody().strength(-400))
        .force('center', d3.forceCenter(this.currentWidth / 2, this.currentHeight / 2))
        .force('collision', d3.forceCollide()
          .radius(d => {
            // Calcule le radius réel basé sur la taille du texte
            const textWidth = Math.max(d.name.length * 8 + 20, 100)
            return textWidth / 2 + 20 // Ajoute une marge de sécurité
          })
          .strength(1.0)
          .iterations(3) // Plus d'itérations pour éviter les superpositions
        )

      // Create link groups
      const linkGroup = g.selectAll('.link-group')
        .data(this.links, d => `${d.source.id || d.source}-${d.target.id || d.target}`)
        .join('g')
        .attr('class', 'link-group')

      // Create links
      const link = linkGroup.selectAll('line')
        .data(d => [d])
        .join('line')
        .attr('class', 'link')
        .attr('stroke', '#333')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)')
        .style('pointer-events', 'none')

      // Create nodes
      const node = g.selectAll('.node')
        .data(this.nodes, d => d.id)
        .join('g')
        .attr('class', 'node')
        .call(this.drag())

      // Add rectangles to nodes for better text display
      node.selectAll('rect').remove()
      node.selectAll('circle').remove()
      
      // Calculate text width for each node (fonction cohérente avec collision)
      const getTextWidth = (text) => Math.max(text.length * 8 + 20, 100)
      const textHeight = 40
      
      node.append('rect')
        .attr('width', d => getTextWidth(d.name))
        .attr('height', textHeight)
        .attr('x', d => -getTextWidth(d.name) / 2)
        .attr('y', -textHeight / 2)
        .attr('fill', d => this.nodeTypes[d.type] || '#95a5a6')
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        .attr('rx', 20) // Rounded corners
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.selectNode(d)
        })

      // Add text to nodes avec police plus grande
      node.selectAll('text').remove()
      node.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em') // Légèrement ajusté pour le centrage
        .attr('font-size', '15px') // Police plus grande
        .attr('fill', 'white')
        .attr('font-weight', 'bold')
        .style('cursor', 'pointer')
        .style('pointer-events', 'all')
        .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.8)') // Ombre pour meilleur contraste
        .text(d => d.name) // Display full name
        .on('click', (event, d) => {
          this.selectNode(d)
        })

      // Add tooltips
      node.append('title')
        .text(d => `${d.name}\nType: ${d.type}`)

      linkGroup.append('title')
        .text(d => `${this.getNodeName(d.source)} → ${this.getNodeName(d.target)}\nType: ${this.linkTypes[d.type] || d.type}`)

      // Update positions on simulation tick
      this.simulation.on('tick', () => {
        // Update link positions
        link
          .attr('x1', d => {
            const dx = d.target.x - d.source.x
            const dy = d.target.y - d.source.y
            const length = Math.sqrt(dx * dx + dy * dy)
            const nodeWidth = Math.max(d.source.name.length * 8 + 20, 100)
            return d.source.x + (dx / length) * (nodeWidth / 2)
          })
          .attr('y1', d => {
            const dx = d.target.x - d.source.x
            const dy = d.target.y - d.source.y
            const length = Math.sqrt(dx * dx + dy * dy)
            return d.source.y + (dy / length) * 20
          })
          .attr('x2', d => {
            const dx = d.target.x - d.source.x
            const dy = d.target.y - d.source.y
            const length = Math.sqrt(dx * dx + dy * dy)
            const nodeWidth = Math.max(d.target.name.length * 8 + 20, 100)
            return d.target.x - (dx / length) * (nodeWidth / 2)
          })
          .attr('y2', d => {
            const dx = d.target.x - d.source.x
            const dy = d.target.y - d.source.y
            const length = Math.sqrt(dx * dx + dy * dy)
            return d.target.y - (dy / length) * 20
          })

        // Update node positions
        node
          .attr('transform', d => `translate(${d.x},${d.y})`)
      })
    },

    drag() {
      function dragstarted(event, d) {
        if (!event.active) this.simulation.alphaTarget(0.3).restart()
        d.fx = d.x
        d.fy = d.y
      }

      function dragged(event, d) {
        d.fx = event.x
        d.fy = event.y
      }

      function dragended(event, d) {
        if (!event.active) this.simulation.alphaTarget(0)
        d.fx = null
        d.fy = null
      }

      return d3.drag()
        .on('start', dragstarted.bind(this))
        .on('drag', dragged)
        .on('end', dragended.bind(this))
    },

    selectNode(node, autoCenter = true) {
      this.selectedNode = node
      this.selectedNodeId = node ? node.id : null // Synchronise le v-select
      this.highlightRelatedElements()
      this.updateGraphVisibility() // Applique le filtre de visibilité
      
      if (autoCenter) {
        // Centre la vue sur le nœud sélectionné sans zoom
        this.centerOnNode(node, null, 500) // null = garde le zoom actuel
      }
    },

    centerOnNode(node, zoomLevel = null, duration = 500) {
      // Vérification que le nœud a des coordonnées valides
      if (!node || node.x === undefined || node.y === undefined) {
        //console.log('Node coordinates not ready:', node)
        return
      }

      //console.log('Centering on node:', node.name, 'at', node.x, node.y)

      // Récupère le zoom actuel si aucun niveau spécifié
      const currentTransform = d3.zoomTransform(this.svg.node())
      const currentZoom = zoomLevel !== null ? zoomLevel : currentTransform.k

      // Calcule la transformation pour centrer le nœud avec le zoom actuel ou spécifié
      const x = -node.x * currentZoom + this.currentWidth / 2
      const y = -node.y * currentZoom + this.currentHeight / 2

      //console.log('Transform values:', { x, y, zoomLevel: currentZoom })

      // Utilise l'instance de zoom sauvegardée
      this.svg.transition()
        .duration(duration)
        .call(
          this.zoomBehavior.transform,
          d3.zoomIdentity.translate(x, y).scale(currentZoom)
        )
    },

    clearSelection() {
      this.selectedNode = null
      this.selectedNodeId = null // Synchronise le v-select
      this.highlightRelatedElements()
      this.updateGraphVisibility() // Réaffiche tous les nœuds
    },

    highlightRelatedElements() {
      const g = this.svg.select('.graph-group')
      
      // Reset all elements to default style - garde la même apparence
      g.selectAll('.node rect')
        .attr('fill', d => this.nodeTypes[d.type] || '#95a5a6') // Réinitialise la couleur d'origine basée sur le type
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        // N'affecte pas l'opacité ici, elle est gérée par updateGraphVisibility

      g.selectAll('.node text')
        // N'affecte pas l'opacité du texte ici non plus

      g.selectAll('.link')
        .attr('stroke', '#333')
        .attr('stroke-width', 2)
        // L'opacité des liens est aussi gérée par updateGraphVisibility
        .attr('marker-end', 'url(#arrowhead)')

      if (this.selectedNode) {
        // Highlight selected node with blue background
        g.selectAll('.node')
          .filter(d => d.id === this.selectedNode.id)
          .select('rect')
          .attr('fill', '#007bff') // Bleu pour le nœud actif
          .attr('stroke', '#0056b3') // Bordure bleu foncé
          .attr('stroke-width', 5)
          .attr('opacity', 1)

        // Highlight related nodes with different colors for incoming/outgoing
        this.outgoingRelations.forEach(relation => {
          const targetId = typeof relation.target === 'object' ? relation.target.id : relation.target
          g.selectAll('.node')
            .filter(d => d.id === targetId)
            .select('rect')
            .attr('stroke', '#28a745') // Vert pour les projets sortants
            .attr('stroke-width', 4)
            .attr('opacity', 1)
        })

        this.incomingRelations.forEach(relation => {
          const sourceId = typeof relation.source === 'object' ? relation.source.id : relation.source
          g.selectAll('.node')
            .filter(d => d.id === sourceId)
            .select('rect')
            .attr('stroke', '#dc3545') // Rouge pour les projets entrants
            .attr('stroke-width', 4)
            .attr('opacity', 1)
        })

        // Highlight outgoing links (green)
        g.selectAll('.link')
          .filter(d => {
            const sourceId = typeof d.source === 'object' ? d.source.id : d.source
            return sourceId === this.selectedNode.id
          })
          .attr('stroke', '#28a745') // Vert pour les liens sortants
          .attr('stroke-width', 4)
          .attr('opacity', this.showOnlyConnectedNodes ? 1 : 1) // Garde opacité normale avec filtre
          .attr('marker-end', 'url(#arrowhead-outgoing)')

        // Highlight incoming links (red)
        g.selectAll('.link')
          .filter(d => {
            const targetId = typeof d.target === 'object' ? d.target.id : d.target
            return targetId === this.selectedNode.id
          })
          .attr('stroke', '#dc3545') // Rouge pour les liens entrants
          .attr('stroke-width', 4)
          .attr('opacity', this.showOnlyConnectedNodes ? 1 : 1) // Garde opacité normale avec filtre
          .attr('marker-end', 'url(#arrowhead-incoming)')
      }
    },

    getNodeName(nodeRef) {
      if (typeof nodeRef === 'object' && nodeRef.name) {
        return nodeRef.name
      }
      // Cherche par uid maintenant (string)
      const node = this.nodes.find(n => n.id === nodeRef)
      return node ? node.name : 'Unknown'
    },

    getNodeId(nodeRef) {
      if (typeof nodeRef === 'object' && nodeRef.id) {
        return nodeRef.id
      }
      return nodeRef
    },

    focusOnNode(nodeId) {
      //console.log('Focusing on node:', nodeId)
      
      // Trouve le nœud dans les données
      const targetNode = this.nodes.find(n => n.id === nodeId)
      if (!targetNode) {
        //console.log('Node not found:', nodeId)
        return
      }

      //console.log('Found node:', targetNode.name, targetNode)

      // Sélectionne le nœud sans centrage automatique
      this.selectNode(targetNode, false)
      
      // Centre sans zoom - garde le niveau de zoom actuel
      setTimeout(() => {
        this.centerOnNode(targetNode, null, 750) // null = pas de changement de zoom
      }, 100)
    },

    getProjectDescription(selectedNode) {
      if (!selectedNode || !selectedNode.project) {
        return null
      }
      
      // Navigue dans la structure: project.scenario.description_project
      const project = selectedNode.project
      if (project.scenario && project.scenario.description_project) {
        return project.scenario.description_project
      }
      
      return null
    },

    getLastTreatmentDate(selectedNode) {
      if (!selectedNode || !selectedNode.project || !selectedNode.project.last_treatment) {
        return null
      }
      
      // Utilise moment pour formater la date en français
      return this.moment(selectedNode.project.last_treatment).format('DD MMMM YYYY à HH:mm')
    },

    getApiCalls(selectedNode) {
      if (!selectedNode || !selectedNode.project || !selectedNode.project.steps_details) {
        return []
      }
      
      const apiCalls = []
      const stepsDetails = selectedNode.project.steps_details
      
      // Cherche dans la section "api" > "urls"
      if (stepsDetails.api && stepsDetails.api.urls) {
        Object.keys(stepsDetails.api.urls).forEach(url => {
          // Extrait la méthode HTTP du début de l'URL
          const parts = url.split(' ')
          if (parts.length >= 2) {
            apiCalls.push({
              method: parts[0], // GET, POST, PATCH, etc.
              url: parts.slice(1).join(' ') // Le reste de l'URL
            })
          }
        })
      }
      
      return apiCalls
    },

    getDatabaseCalls(selectedNode) {
      if (!selectedNode || !selectedNode.project || !selectedNode.project.steps_details) {
        return []
      }
      
      const dbCalls = []
      const stepsDetails = selectedNode.project.steps_details
      
      // Cherche dans la section "db" > "db"
      if (stepsDetails.db && stepsDetails.db.db) {
        Object.keys(stepsDetails.db.db).forEach(query => {
          // Extrait l'opération et la table du query
          const parts = query.split(' ')
          if (parts.length >= 2) {
            const tableValue = stepsDetails.db.db[query]
            dbCalls.push({
              operation: parts[0], // SELECT, UPDATE, INSERT, etc.
              table: typeof tableValue === 'string' ? tableValue : String(tableValue || ''), // S'assure que c'est une chaîne
              fullQuery: query // La requête complète
            })
          }
        })
      }
      
      return dbCalls
    },

    getAllConnectedNodes(nodeId) {
      if (!nodeId) return new Set()
      
      const visited = new Set()
      
      // Fonction récursive pour suivre les chaînes vers l'avant (relations sortantes)
      const followOutgoing = (currentId) => {
        if (visited.has(currentId)) return
        visited.add(currentId)
        
        this.links.forEach(link => {
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source
          const targetId = typeof link.target === 'object' ? link.target.id : link.target
          
          if (sourceId === currentId && !visited.has(targetId)) {
            followOutgoing(targetId)
          }
        })
      }
      
      // Fonction récursive pour suivre les chaînes vers l'arrière (relations entrantes)
      const followIncoming = (currentId) => {
        if (visited.has(currentId)) return
        visited.add(currentId)
        
        this.links.forEach(link => {
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source
          const targetId = typeof link.target === 'object' ? link.target.id : link.target
          
          if (targetId === currentId && !visited.has(sourceId)) {
            followIncoming(sourceId)
          }
        })
      }
      
      // Commence par le nœud sélectionné
      visited.add(nodeId)
      
      // Suit toutes les chaînes sortantes
      this.links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source
        const targetId = typeof link.target === 'object' ? link.target.id : link.target
        
        if (sourceId === nodeId && !visited.has(targetId)) {
          followOutgoing(targetId)
        }
      })
      
      // Suit toutes les chaînes entrantes
      this.links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source
        const targetId = typeof link.target === 'object' ? link.target.id : link.target
        
        if (targetId === nodeId && !visited.has(sourceId)) {
          followIncoming(sourceId)
        }
      })
      
      return visited
    },

    updateGraphVisibility() {
      if (!this.svg) return
      
      const g = this.svg.select('.graph-group')
      
      // Détermine quels nœuds doivent être visibles
      let visibleNodeIds = new Set()
      
      if (this.showOnlyConnectedNodes && this.selectedNode) {
        // Mode "nœuds connectés" : utilise les nœuds connectés au nœud sélectionné
        visibleNodeIds = this.getAllConnectedNodes(this.selectedNode.id)
      } else {
        // Mode normal : utilise les nœuds filtrés
        this.filteredNodes.forEach(node => visibleNodeIds.add(node.id))
      }
      
      // Applique la visibilité des nœuds
      g.selectAll('.node')
        .style('opacity', d => {
          if (this.showOnlyConnectedNodes && this.selectedNode) {
            // Mode "nœuds connectés" : affiche ou cache complètement
            if (visibleNodeIds.has(d.id)) {
              // Nœud connecté visible : vérifier s'il correspond aux filtres
              if (this.hasActiveFilters) {
                // Si des filtres sont actifs, réduit l'opacité si le nœud ne correspond pas
                return this.nodeMatchesFilters(d) ? 1 : 0.4
              } else {
                // Pas de filtres actifs : opacité complète
                return 1
              }
            } else {
              // Nœud non connecté : invisible
              return 0
            }
          } else {
            // Mode normal : utilise le système d'opacité pour les filtres
            if (visibleNodeIds.has(d.id)) {
              // Nœud visible : opacité complète si correspond aux filtres, réduite mais lisible sinon
              return this.nodeMatchesFilters(d) ? 1 : 0.6
            } else {
              // Nœud non visible
              return 0.2
            }
          }
        })
        .style('pointer-events', d => {
          if (this.showOnlyConnectedNodes && this.selectedNode) {
            // Mode "nœuds connectés" : events seulement sur les nœuds visibles
            return visibleNodeIds.has(d.id) ? 'all' : 'none'
          } else {
            // Mode normal : events sur tous les nœuds visibles
            return visibleNodeIds.has(d.id) ? 'all' : 'none'
          }
        })
      
      // Applique la visibilité des liens
      g.selectAll('.link')
        .style('opacity', d => {
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source
          const targetId = typeof d.target === 'object' ? d.target.id : d.target
          
          if (this.showOnlyConnectedNodes && this.selectedNode) {
            // Mode "nœuds connectés" : affiche les liens entre nœuds connectés
            return (visibleNodeIds.has(sourceId) && visibleNodeIds.has(targetId)) ? 1 : 0
          } else {
            // Mode normal : réduit l'opacité si l'un des nœuds ne correspond pas aux filtres
            const sourceNode = this.allNodes.find(n => n.id === sourceId)
            const targetNode = this.allNodes.find(n => n.id === targetId)
            
            if (!sourceNode || !targetNode) return 0.1
            
            const sourceMatches = this.nodeMatchesFilters(sourceNode)
            const targetMatches = this.nodeMatchesFilters(targetNode)
            
            if (sourceMatches && targetMatches) {
              return 1
            } else if (sourceMatches || targetMatches) {
              return 0.5
            } else {
              return 0.2
            }
          }
        })
      
      // Reapplique la mise en surbrillance si un nœud est sélectionné
      if (this.selectedNode) {
        this.highlightRelatedElements()
      }
    },

    resetZoom() {
      this.svg.transition().duration(750).call(
        this.zoomBehavior.transform,
        d3.zoomIdentity
      )
    },

    showDescriptionModal() {
      if (this.selectedNode && this.getProjectDescription(this.selectedNode)) {
        this.showDescriptionModalFlag = true
      }
    }
  }
}
</script>

<style scoped>
.project-graph-container {
  height: 100vh;
}

.reference-date-info {
  text-align: right;
  border-left: 3px solid #007bff;
  padding-left: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 8px 12px;
}

.reference-date-info small {
  font-size: 0.75rem;
  line-height: 1.2;
}

.reference-date-info strong {
  font-size: 0.9rem;
  display: inline-block;
  margin-top: 2px;
}

.main-row {
  height: calc(100vh - 120px);
}

.graph-section,
.details-section {
  height: 100%;
}

.graph-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  padding: 0;
  margin: 0;
}

.details-panel {
  transition: all 0.3s ease;
  border: 1px solid #dee2e6 !important;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.details-panel.selected {
  box-shadow: 0 3px 12px rgba(0, 123, 255, 0.15);
  border-color: #007bff !important;
}

.compact-body {
  padding: 0 !important;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.project-header {
  background: #f8f9fa;
  color: #495057;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #dee2e6;
  text-align: center;
  border-radius: 6px 6px 0 0;
  flex-shrink: 0;
}

.project-header .card-title {
  color: #495057 !important;
  font-weight: 500;
  margin: 0;
  font-size: 1rem;
}

.project-content {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* Scrollbar moderne et discrète pour le contenu du projet */
.project-content::-webkit-scrollbar {
  width: 4px;
}

.project-content::-webkit-scrollbar-track {
  background: transparent;
}

.project-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.project-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

.type-badge {
  color: white !important;
  border: none;
}

.description-container {
  max-height: 85px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;
  line-height: 1.3;
  color: #495057;
  white-space: pre-wrap;
  transition: all 0.2s ease;
}

.description-container:hover {
  background-color: #e9ecef;
  border-color: #007bff;
  box-shadow: 0 1px 3px rgba(0, 123, 255, 0.1);
}

.description-modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

.description-modal-content p {
  margin: 0;
  color: #495057;
}

.description-container::-webkit-scrollbar {
  width: 6px;
}

.description-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.description-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.description-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Styles pour les appels API - Design neutre */
.api-calls-container {
  max-height: 120px;
  overflow-y: auto;
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
  border-radius: 4px;
  padding: 10px;
}

.api-call-item {
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.api-call-item:last-child {
  border-bottom: none;
}

.api-call-item .fw-medium {
  color: #1565c0;
  font-weight: 600;
}

.api-call-item .text-muted {
  color: #757575 !important;
}

.api-calls-container::-webkit-scrollbar {
  width: 4px;
}

.api-calls-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.api-calls-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

/* Styles pour les appels en base */
/* Styles pour les appels DB - Design neutre */
.db-calls-container {
  max-height: 120px;
  overflow-y: auto;
  background: #fce4ec;
  color: #c2185b;
  border: 1px solid #f8bbd9;
  border-radius: 4px;
  padding: 10px;
}

.db-call-item {
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.db-call-item:last-child {
  border-bottom: none;
}

.db-call-item .fw-medium {
  color: #c2185b;
  font-weight: 600;
}

.db-call-item .text-muted {
  color: #757575 !important;
}

.db-calls-container::-webkit-scrollbar {
  width: 4px;
}

.db-calls-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.db-calls-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.relations-list {
  max-height: 200px;
  overflow-y: auto;
}

.relations-list .list-group-item {
  border-left: none;
  border-right: none;
}

.relations-list .list-group-item:first-child {
  border-top: none;
}

.relations-list .list-group-item:last-child {
  border-bottom: none;
}

/* Style pour les relations cliquables */
.clickable-relation {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-relation:hover {
  background-color: #f8f9fa !important;
}

/* Style pour les relations filtrées - Amélioré pour la lisibilité */
.filtered-out {
  opacity: 0.65;
  background-color: #f8f9fa !important;
}

.filtered-out .fw-medium {
  font-weight: 400 !important;
  color: #6c757d !important;
}

.filtered-out:hover {
  opacity: 0.85;
  background-color: #e9ecef !important;
}

/* Styles pour les filtres repliables - Nouvelle version */
.filter-panel {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.gap-2 > * {
  margin-right: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

/* Style pour le switch personnalisé */
.custom-switch {
  font-size: 14px;
  font-weight: 500;
}

.custom-switch .form-check-input {
  width: 2.5rem;
  height: 1.5rem;
  border-radius: 1rem;
}

.custom-switch .form-check-label {
  padding-left: 0.5rem;
  color: #495057;
}

/* Styles pour les éléments D3 */
:deep(.node) {
  cursor: pointer;
}

:deep(.node:hover rect) {
  stroke-width: 5px;
}

:deep(.link-group) {
  pointer-events: none;
}

:deep(.link) {
  pointer-events: none;
}

:deep(.link-label) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
</style>